import { z } from "zod";

// ---------------------------------------------------------------------------
// Wire shapes
//
// Mirror the records in `Common/Realtime/RealtimeNotifications.cs`. Hub
// payloads arrive via SignalR.HubConnection.on(eventName, handler) as a
// single object argument; the schemas defend against contract drift in the
// same way the rest of the experience-shell defends against backend
// responses — Zod parse at the network boundary, plain values everywhere
// inside.
//
// `AsOf` is an ISO-8601 timestamp (System.Text.Json's default for
// `DateTimeOffset`). It is captured so consumers can compare against the
// timestamps already carried by `QueueWorkItemCountsResponse` and decide
// whether a push pre-empts or trails an in-flight refetch.
// ---------------------------------------------------------------------------

// The two queue lists carry distinct meanings (visibility vs.
// awaiting-action) and are both routed through realtime so cache
// patches can update both the listing/timeline view and the queue
// tally badge without a follow-up fetch. See
// experience-shell/workflows/types.ts for the WorkItem schema docs
// that explain the asymmetry on spawn/Ask items.
export const WorkItemAddedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    type: z.string(),
    visibleToQueueIds: z.array(z.string().uuid()),
    awaitingActionByQueueIds: z.array(z.string().uuid()),
    asOf: z.string(),
});

export type WorkItemAddedNotification = z.infer<typeof WorkItemAddedNotificationSchema>;

export const WorkItemCompletedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    awaitingActionByQueueIds: z.array(z.string().uuid()),
    completedBy: z.string().nullable().optional(),
    completedByDisplayName: z.string().nullable().optional(),
    completedAt: z.string(),
    asOf: z.string(),
});

export type WorkItemCompletedNotification = z.infer<typeof WorkItemCompletedNotificationSchema>;

// Due notification. Fired by the time-driven overdue sweep when an item
// crosses its due date (Pending -> Open) without being completed. Carries
// both queue lists for the same reason as Added/Completed: the Open badge
// ticks up (awaiting-action-driven) and the listing order can shift
// (visibility-driven). `dueDate` is the crossed instant; like the others it
// carries no full WorkItem shape, so the receiver invalidates rather than
// patches.
export const WorkItemDueNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    awaitingActionByQueueIds: z.array(z.string().uuid()),
    dueDate: z.string(),
    asOf: z.string(),
});

export type WorkItemDueNotification = z.infer<typeof WorkItemDueNotificationSchema>;

// Snooze notification. Carries the post-state `newDueDate` so the
// receiving cache can patch the row in place (the only field that
// changes on a snooze), plus `previousDueDate` so consumers that
// animate the chip transition can show the prior value without a
// follow-up fetch. `snoozedBy` / `snoozedByDisplayName` mirror the
// completion notification's actor snapshot so the timeline can render
// "Snoozed by …" without a queue-membership round-trip.
export const WorkItemSnoozedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    awaitingActionByQueueIds: z.array(z.string().uuid()),
    previousDueDate: z.string().nullable(),
    newDueDate: z.string(),
    snoozedBy: z.string().nullable().optional(),
    snoozedByDisplayName: z.string().nullable().optional(),
    asOf: z.string(),
});

export type WorkItemSnoozedNotification = z.infer<typeof WorkItemSnoozedNotificationSchema>;

// Lock notification (docs/NEXT.md — Phase 1). Fans out to the item's
// visibility audience only (no awaiting-action / badge semantics), so it
// carries a single queue list. `lockedUntil` lets the receiving cache age
// the lock out locally even before the matching unlock event arrives.
export const WorkItemLockedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    lockedBy: z.string(),
    lockedByDisplayName: z.string().nullable().optional(),
    lockedUntil: z.string(),
    asOf: z.string(),
});

export type WorkItemLockedNotification = z.infer<typeof WorkItemLockedNotificationSchema>;

// Unlock notification. Carries no holder — the item is simply free as of
// `asOf`. Emitted on completion, snooze, scheduled expiry, and the
// backstop sweeper.
export const WorkItemUnlockedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    asOf: z.string(),
});

export type WorkItemUnlockedNotification = z.infer<typeof WorkItemUnlockedNotificationSchema>;

// Edited notification. Today only the note edit emits this. Carries the
// post-edit `body` so the receiving cache can patch the row in place (the
// only field a body edit changes), plus `previousBody` so consumers can
// annotate the change without a follow-up fetch. `editedBy` /
// `editedByDisplayName` mirror the other events' actor snapshot. Fans out
// to the visibility audience only (no awaiting-action / badge semantics).
export const WorkItemEditedNotificationSchema = z.object({
    workItemId: z.string().uuid(),
    workflowId: z.string().uuid(),
    visibleToQueueIds: z.array(z.string().uuid()),
    previousBody: z.string().nullable().optional(),
    body: z.string().nullable().optional(),
    editedBy: z.string().nullable().optional(),
    editedByDisplayName: z.string().nullable().optional(),
    asOf: z.string(),
});

export type WorkItemEditedNotification = z.infer<typeof WorkItemEditedNotificationSchema>;

// Cross-app navigation acknowledgement. Pushed back to the originating tab once
// the user's designated Galileo tab has handled a navigation intent, so the
// sender suppresses its window.open fallback. Mirrors
// `PeerNavigateAckNotification` in the Einstein API. Only `correlationId` is
// carried — it is matched against the pending intent the sender remembers.
export const PeerNavigateAckSchema = z.object({
    correlationId: z.string().min(1),
});

export type PeerNavigateAck = z.infer<typeof PeerNavigateAckSchema>;

// Cross-app navigation intent — the receiver side (Galileo -> Einstein). Pushed
// to every Einstein connection of the user when a Galileo tab (the load ribbon's
// Einstein control) asks to open an Einstein deep-link; only the user's
// designated recipient tab (the installed PWA) acts on it. Mirrors the Einstein
// API's `PeerNavigateIntentNotification` and the Galileo API's
// `PeerNavigateIntentPayload`. `replyConnectionId` is echoed back through
// `AckPeerNavigation` so the originating Galileo tab's window.open fallback is
// cancelled with no server-side correlation state.
export const PeerNavigateIntentSchema = z.object({
    path: z.string().min(1),
    correlationId: z.string().min(1),
    source: z.string().min(1),
    replyConnectionId: z.string().min(1),
});

export type PeerNavigateIntent = z.infer<typeof PeerNavigateIntentSchema>;

// ---------------------------------------------------------------------------
// Hub method / event names
//
// String constants over enums to mirror the wire exactly and to give us a
// single place to grep when a contract changes. Names match
// `RealtimePublisher.WorkItemAddedEvent` and `WorkItemCompletedEvent` and
// `WorkflowHub.JoinWorkflow` / `LeaveWorkflow` on the server.
// ---------------------------------------------------------------------------

export const RealtimeEvents = {
    workItemAdded: "WorkItemAdded",
    workItemCompleted: "WorkItemCompleted",
    workItemDue: "WorkItemDue",
    workItemSnoozed: "WorkItemSnoozed",
    workItemLocked: "WorkItemLocked",
    workItemUnlocked: "WorkItemUnlocked",
    workItemEdited: "WorkItemEdited",
    peerNavigateAck: "PeerNavigateAck",
    peerNavigateIntent: "PeerNavigateIntent",
} as const;

export const RealtimeHubMethods = {
    joinWorkflow: "JoinWorkflow",
    leaveWorkflow: "LeaveWorkflow",
    ping: "Ping",
    requestPeerNavigation: "RequestPeerNavigation",
    ackPeerNavigation: "AckPeerNavigation",
} as const;
